import PropTypes from "prop-types";
import "./Pagination.css";

const LEFT_ELLIPSIS = "left-ellipsis";
const RIGHT_ELLIPSIS = "right-ellipsis";

// Inclusive integer range [start, end].
const range = (start, end) => {
  const length = end - start + 1;
  return length > 0 ? Array.from({ length }, (_, i) => start + i) : [];
};

/**
 * Build the list of pagination items for the given page count.
 * Always keeps the first and last page visible and shows a sliding window of
 * `siblingCount` pages on each side of the current page, inserting ellipsis
 * markers where pages are skipped. Scales cleanly to tens/hundreds of pages.
 *
 * Returns an array of page numbers and ellipsis marker strings.
 */
const getPaginationRange = (currentPage, totalPages, siblingCount = 1) => {
  // first + last + current + 2 ellipsis slots + siblings on both sides
  const totalItems = siblingCount * 2 + 5;

  // Few enough pages that everything fits — show them all.
  if (totalPages <= totalItems) {
    return range(1, totalPages);
  }

  const leftSibling = Math.max(currentPage - siblingCount, 1);
  const rightSibling = Math.min(currentPage + siblingCount, totalPages);

  // Show an ellipsis only when it hides more than one page.
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  const edgeItemCount = 3 + 2 * siblingCount;

  if (!showLeftEllipsis && showRightEllipsis) {
    return [...range(1, edgeItemCount), RIGHT_ELLIPSIS, totalPages];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    return [
      1,
      LEFT_ELLIPSIS,
      ...range(totalPages - edgeItemCount + 1, totalPages),
    ];
  }

  return [
    1,
    LEFT_ELLIPSIS,
    ...range(leftSibling, rightSibling),
    RIGHT_ELLIPSIS,
    totalPages,
  ];
};

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  onPrevious,
  onNext,
}) => {
  const renderPageNumbers = () => {
    // Nothing to page through.
    if (!totalPages || totalPages <= 1) {
      return null;
    }

    return getPaginationRange(currentPage, totalPages).map((item) => {
      if (item === LEFT_ELLIPSIS || item === RIGHT_ELLIPSIS) {
        return <span key={item}>...</span>;
      }

      return (
        <button
          key={item}
          className={`pagination-page ${currentPage === item ? "active" : ""}`}
          onClick={() => onPageChange(item)}
        >
          {item}
        </button>
      );
    });
  };

  return (
    <div className="pagination">
      <button
        className="pagination-button pagination-button--prev"
        onClick={onPrevious}
        disabled={currentPage === 1}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 12L6 8L10 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Previous
      </button>
      <div className="pagination-pages">{renderPageNumbers()}</div>
      <button
        className="pagination-button pagination-button--next"
        onClick={onNext}
        disabled={currentPage === totalPages}
      >
        Next
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 12L10 8L6 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  total: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default Pagination;
